<template>
    <div class="report-template">
		<ReportInfo @onUserInfoRender="checkDomRender()" :data="data"/>

		<component
			class="chart-item"
			v-for="(intervalItem, key) in intervalData"
			:key="key"
			:tableName="intervalItem.label"
			:subHeader="intervalItem.subHeader"
			:dataType="intervalItem.dataType || ''"
			:labels="intervalItem.labels"

			:dataSet="intervalItem.dataSet"
			:color="intervalItem.color"
			:emotionLevels="intervalItem.emotionLevels"
			:stressLevels="intervalItem.stressLevels"
			:is="intervalItem.chartType"
		/>

		<!-- show only when report includes activity -->
		<div
			class="time-stamp"
			v-if="data.activity_history ? data.activity_history.length : false"
		>
			<ReportActivityTimeStamp
				:activities="data.activity_history"
			/>
		</div>
    </div>
</template>

<script>
import moment from 'moment'
import ReportInfo from '@/components/pdf-report/ReportInfo.vue'
import ReportLineChart from '@/components/pdf-report/ReportLineChart.vue'
import ReportRadarChart from '@/components/pdf-report/ReportRadarChart.vue'
import ReportActivityTimeStamp from './ReportActivityTimeStamp'

export default {
	props: {
		data: {
			type: Object
		}
	},

	data () {
		return {
			domRendered: false,
			hasFinallyRendered: false
		}
	},

	mounted () {
		this.$nextTick(() => {
			this.checkDomRender('dom')
			this.domRendered = true
		})
	},

	computed: {

		// ...mapGetters({
		// 	reportText: 'getReportTranslation',
		// 	reportTextAuth: 'getAuthReportTranslation'
		// }),

		intervalData () {
			const keyList = [
				'per_hour_emotion', 'per_minute_emotion',
				'per_hour_stress', 'per_minute_stress',
				'emotion_count_radar'
			]

			let intervalData = []

			keyList.forEach((key) => {
				if (!this.data[key]) return

				let chartData = []

				if (this.data[key].series.length < 1) {
					chartData.push({
						chartType: this.setChartType(key),

						label: this.setChartLabel(key),

						dataType: this.setDataType(key),

						color: this.setColor(key),

						emotionLevels: this.setEmotionLevels(this.data[key].labels, key)
					})
				} else {
					chartData = this.data[key].series[0].data.reduce((total, item) => {
						total.push({
							chartType: this.setChartType(key),

							label: this.setChartLabel(key),

							dataType: this.setDataType(key),

							color: this.setColor(key),

							subHeader: this.setSubHeader(item, key),

							emotionLevels: this.setEmotionLevels(this.data[key].labels, key),

							labels: this.setXAxisLabels(item, key),

							dataSet: this.setDataSet(item, key),

							stressLevels: this.setStressLevels(item)
						})

						return total
					}, [])
				}

				intervalData = [...intervalData, ...chartData]
			})

			return intervalData
		}
	},

	methods: {
		/* Set Chart Data Methods */
		setChartType (keyName) {
			return keyName === 'emotion_count_radar'
				? 'ReportRadarChart'
				: 'ReportLineChart'
		},

		setChartLabel (keyName) {
			const { session_type } = this.data

			switch (keyName) {
			case 'per_hour_emotion':
				return session_type === 'Movie'
					? this.$t('frequent_emotion_per_hour')
					: this.$t('average_emotion_hour')

			case 'per_minute_emotion':
				return this.$t('average_emotion_minute')

			case 'per_hour_stress':
				return this.$t('average_stress_level_hour')

			case 'per_minute_stress':
				return this.$t('average_stress_level_minute')
			case 'emotion_count_radar':
				return this.$t('emotion_distribution')
			}
		},

		setDataType (keyName) {
			const stressLevelKeys = ['per_hour_stress', 'per_minute_stress']

			return stressLevelKeys.indexOf(keyName) > -1
				? 'stress'
				: null
		},

		setColor (keyName) {
			const stressLevelKeys = ['per_hour_stress', 'per_minute_stress']

			return stressLevelKeys.indexOf(keyName) > -1
				? ['#FDEA84']
				: null
		},

		setSubHeader (chartItem, keyName) {
			// For Radar Chart
			if (keyName === 'emotion_count_radar') {
				// if (chartItem[0].total_emotion_per_user.interval) {
				// 	return `
				// 		${moment(chartItem[0].date).format('MMMM DD, YYYY')} |
				// 		${chartItem[0].total_emotion_per_user.interval}
				// 	`
				// }

				return `
					${moment(chartItem.date).format('MMMM DD, YYYY')}
				`
			}

			// For Line Chart
			if (chartItem[0].interval) {
				return `
					${moment(chartItem[0].datetime).format('MMMM DD, YYYY')} | 
					${chartItem[0].interval}
				`
			}

			return `
				${moment(chartItem[0].datetime).format('MMMM DD, YYYY')}
			`
		},

		setEmotionLevels (emotionLevels, keyName) {
			return keyName === 'emotion_count_radar'
				? emotionLevels.map(i => this.$t(String(i).toLowerCase()))
				: emotionLevels.slice().map(i => this.$t(String(i).toLowerCase()))
		},

		setXAxisLabels (chartItem, key) {
			if (key !== 'emotion_count_radar') {
				return chartItem.reduce((result, chartData) => {
					result.push(moment(chartData.datetime).format('hh:mm a'))

					return result
				}, [])
			}
		},

		setDataSet (chartItem, keyName) {
			if (keyName === 'emotion_count_radar') {
				const totalSession = chartItem.value.overall
				const user = chartItem.value.selected_users
				return {totalSession, user}
			}

			return chartItem.reduce((dataSetArray, chartData) => {
				dataSetArray.push({
					y: chartData.value
				})

				return dataSetArray
			}, [])
		},

		setStressLevels () {
			return [
				this.$t('low'),
				this.$t('mild'),
				this.$t('normal'),
				this.$t('high')
			]
		},

		checkDomRender () {
            console.log(this.domRendered)
            console.log(!this.hasFinallyRendered)
			if (
				this.domRendered &&
				!this.hasFinallyRendered
			) {
				this.hasFinallyRendered = true
				this.$emit('domRendered')
			}
		}
	},

	components: {
		ReportInfo,
		ReportLineChart,
		ReportRadarChart,
		ReportActivityTimeStamp
	}
}
</script>

<style lang="scss" scoped>
.report-template {
    width: 790px;
	background: #fff;

	.time-stamp {
		padding: 20px 0;
		width: 100%;
		flex-shrink: 0;
	}

    .chart-item {
		width: 100%;
		flex-shrink: 0;
		padding-top: 10px;
		margin-top: 10px;
		border-top: 1px solid #ebebeb;

		.date-time {
			color: red;
		}

        /deep/ .chart-wrapper {
            height: auto;
        }

        /deep/ h2 {
            padding: 10px 0 5px 0;
			font-size: 13px;
        }

		/deep/ h4 {
			padding-top: 10px;
			font-size: 13px;
		}

		/deep/ .sub-header {
			font-size: 13px;
			padding: 2px 0 0 0;
		}
    }
}
</style>
